import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { NowPlayingProvider } from "./context/NowPlayingContext";
import NowPlayingBar from "./components/NowPlayingBar";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtected from "./admin/components/AdminProtected";
import useAppVersion from "./hooks/useAppVersion";

import SplashScreen from "./screens/SplashScreen";
import OnboardingScreen from "./screens/OnboardingScreen";
import LoginScreen from "./screens/LoginScreen";
import ForgotPasswordScreen from "./screens/ForgotPasswordScreen";
import ResetPasswordScreen from "./screens/ResetPasswordScreen";
import AuthCallbackScreen from "./screens/AuthCallbackScreen";
import HomeScreen from "./screens/HomeScreen";
import IdentifyScreen from "./screens/IdentifyScreen";
import IdentifyResultScreen from "./screens/IdentifyResultScreen";
import DiagnoseResultScreen from "./screens/DiagnoseResultScreen";
import SearchScreen, { ExploreListScreen } from "./screens/SearchScreen";
import HealingScreen from "./screens/HealingScreen";
import HealingDetailScreen from "./screens/HealingDetailScreen";
import JournalScreen from "./screens/JournalScreen";
import JournalEntryScreen from "./screens/JournalEntryScreen";
import MusicScreen from "./screens/MusicScreen";
import CollectionScreen from "./screens/CollectionScreen";
import NotificationsScreen from "./screens/NotificationsScreen";
import SubscriptionScreen from "./screens/SubscriptionScreen";
import SubscriptionSuccessScreen from "./screens/SubscriptionSuccessScreen";
import SpeciesDetailScreen from "./screens/SpeciesDetailScreen";
import PlaylistDetailScreen from "./screens/PlaylistDetailScreen";
import ProfileScreen from "./screens/ProfileScreen";
import AboutScreen from "./screens/AboutScreen";
import ContactScreen from "./screens/ContactScreen";

import AdminLoginScreen from "./admin/screens/AdminLoginScreen";
import AdminDashboard from "./admin/screens/DashboardScreen";
import AdminUsers from "./admin/screens/UsersScreen";
import AdminUserDetail from "./admin/screens/UserDetailScreen";
import AdminSubscriptions from "./admin/screens/SubscriptionsScreen";
import AdminPush from "./admin/screens/PushNotificationsScreen";
import AdminEmail from "./admin/screens/EmailCampaignsScreen";
import AdminMusic from "./admin/screens/MusicLibraryScreen";
import AdminAds from "./admin/screens/AdsScreen";
import AdminSettings from "./admin/screens/SettingsScreen";
import AdminAudit from "./admin/screens/AuditLogScreen";
import AdminAdmins from "./admin/screens/AdminUsersScreen";

function AppRouter() {
  const location = useLocation();
  useAppVersion();
  if (location.hash?.includes("session_id=")) return <AuthCallbackScreen />;
  return (
    <Routes>
      <Route path="/" element={<SplashScreen />} />
      <Route path="/onboarding" element={<OnboardingScreen />} />
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
      <Route path="/reset-password" element={<ResetPasswordScreen />} />
      <Route path="/auth/callback" element={<AuthCallbackScreen />} />

      <Route path="/home" element={<ProtectedRoute><HomeScreen /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchScreen /></ProtectedRoute>} />
      <Route path="/explore/category/:id" element={<ProtectedRoute><ExploreListScreen kind="category" /></ProtectedRoute>} />
      <Route path="/explore/collection/:id" element={<ProtectedRoute><ExploreListScreen kind="collection" /></ProtectedRoute>} />
      <Route path="/species/:slug" element={<ProtectedRoute><SpeciesDetailScreen /></ProtectedRoute>} />

      <Route path="/healing" element={<ProtectedRoute><HealingScreen /></ProtectedRoute>} />
      <Route path="/healing/:diseaseId" element={<ProtectedRoute><HealingDetailScreen /></ProtectedRoute>} />

      <Route path="/identify" element={<ProtectedRoute><IdentifyScreen mode="identify" /></ProtectedRoute>} />
      <Route path="/identify/result/:scanId" element={<ProtectedRoute><IdentifyResultScreen /></ProtectedRoute>} />

      <Route path="/diagnose" element={<ProtectedRoute><IdentifyScreen mode="diagnose" /></ProtectedRoute>} />
      <Route path="/diagnose/result/:scanId" element={<ProtectedRoute><DiagnoseResultScreen /></ProtectedRoute>} />

      <Route path="/journal" element={<ProtectedRoute><JournalScreen /></ProtectedRoute>} />
      <Route path="/journal/:plantId" element={<ProtectedRoute><JournalEntryScreen /></ProtectedRoute>} />

      <Route path="/music" element={<ProtectedRoute><MusicScreen /></ProtectedRoute>} />
      <Route path="/music/playlists/:id" element={<ProtectedRoute><PlaylistDetailScreen /></ProtectedRoute>} />
      <Route path="/collection" element={<ProtectedRoute><CollectionScreen /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsScreen /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionScreen /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessScreen /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} />

      <Route path="/about" element={<AboutScreen type="about" />} />
      <Route path="/contact" element={<ContactScreen />} />
      <Route path="/privacy" element={<AboutScreen type="privacy" />} />
      <Route path="/terms" element={<AboutScreen type="terms" />} />

      {/* Admin */}
      <Route path="/admin/login" element={<AdminLoginScreen />} />
      <Route path="/admin" element={<AdminProtected><AdminDashboard /></AdminProtected>} />
      <Route path="/admin/users" element={<AdminProtected><AdminUsers /></AdminProtected>} />
      <Route path="/admin/users/:userId" element={<AdminProtected><AdminUserDetail /></AdminProtected>} />
      <Route path="/admin/subscriptions" element={<AdminProtected><AdminSubscriptions /></AdminProtected>} />
      <Route path="/admin/notifications/push" element={<AdminProtected><AdminPush /></AdminProtected>} />
      <Route path="/admin/notifications/email" element={<AdminProtected><AdminEmail /></AdminProtected>} />
      <Route path="/admin/music" element={<AdminProtected><AdminMusic /></AdminProtected>} />
      <Route path="/admin/ads" element={<AdminProtected><AdminAds /></AdminProtected>} />
      <Route path="/admin/settings" element={<AdminProtected><AdminSettings /></AdminProtected>} />
      <Route path="/admin/audit-log" element={<AdminProtected><AdminAudit /></AdminProtected>} />
      <Route path="/admin/admins" element={<AdminProtected roles={["super_admin"]}><AdminAdmins /></AdminProtected>} />

      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <BrowserRouter>
          <NowPlayingProvider>
            <AppRouter />
            <NowPlayingBar />
            <Toaster
              position="bottom-center"
              theme="dark"
              toastOptions={{
                style: {
                  background: "rgba(20,20,20,0.92)",
                  border: "1px solid rgba(76,175,80,0.35)",
                  color: "#fff",
                  backdropFilter: "blur(16px)",
                },
              }}
            />
          </NowPlayingProvider>
        </BrowserRouter>
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;
